'use client';

import { load, trackPageview, trackEvent } from 'fathom-client';
import { useEffect, useRef, Suspense } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';
import { fathom } from '@/../config';

function TrackPageView() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const isInitialized = useRef(false);

  // Initialize Fathom once (prevents double initialization in React Strict Mode)
  useEffect(() => {
    if (!isInitialized.current) {
      load(fathom.siteid, {
        auto: false,
        spa: 'auto' // Enable SPA mode for proper route change tracking
      });
      isInitialized.current = true;
    }
  }, []);

  // Track pageviews on route changes
  useEffect(() => {
    if (!pathname) return;

    // Build full URL with search params
    const searchString = searchParams?.toString();
    const url = searchString ? `${pathname}?${searchString}` : pathname;
    
    // Let Fathom handle referrer automatically - DO NOT pass document.referrer
    // This prevents self-referrals in SPAs
    trackPageview({
      url: url
    });
  }, [pathname, searchParams]);

  // Track button clicks (optimized with event delegation)
  useEffect(() => {
    const handleButtonClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      const button = target.closest('button, a[role="button"]');
      
      if (button) {
        const buttonText = button.textContent?.trim() ||
                          button.getAttribute('aria-label') ||
                          button.getAttribute('title') ||
                          'Unknown Button';
        
        // Only track if we have meaningful text (avoid tracking empty buttons)
        if (buttonText && buttonText !== 'Unknown Button') {
          trackEvent(`Button: ${buttonText}`);
        }
      }
    };

    // Use capture phase for better performance with event delegation
    document.addEventListener('click', handleButtonClick, { capture: true });
    
    return () => {
      document.removeEventListener('click', handleButtonClick, { capture: true });
    };
  }, []);

  // Track scroll depth (optimized with throttling and initial load check)
  useEffect(() => {
    const scrollDepths = [25, 50, 75, 100];
    const trackedDepths = new Set<number>();
    let isInitialLoad = true;
    let ticking = false;

    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          const windowHeight = window.innerHeight;
          const documentHeight = document.documentElement.scrollHeight;
          const scrollTop = window.scrollY;
          const scrollPercent = ((scrollTop + windowHeight) / documentHeight) * 100;

          // Skip tracking on initial load unless user has actually scrolled
          if (isInitialLoad && scrollTop === 0) {
            isInitialLoad = false;
            ticking = false;
            return;
          }
          
          isInitialLoad = false;

          scrollDepths.forEach(depth => {
            if (scrollPercent >= depth && !trackedDepths.has(depth)) {
              trackedDepths.add(depth);
              trackEvent(`Scroll Depth: ${depth}%`);
            }
          });

          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return null;
}

export default function Fathom() {
  return (
    <Suspense fallback={null}>
      <TrackPageView />
    </Suspense>
  );
}
